import { useEffect } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/shared/store/auth';
import { AUTH_EXPIRED_EVENT } from '@/shared/api/client';

/**
 * Guard de rutas protegidas. Redirige a /login cuando:
 *  - El store no tiene usuario tras hidratarse desde sessionStorage.
 *  - El cliente API emite `auth:expired` (HTTP 401 desde el backend).
 */
export function RequireAuth(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const isHydrated = useAuthStore((s) => s.isHydrated);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onExpired = (): void => {
      logout();
      navigate('/login', { replace: true, state: { from: location.pathname } });
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, [logout, navigate, location.pathname]);

  if (!isHydrated) {
    // Esperar a que `persist` complete la hidratación de sessionStorage.
    return (
      <div className="min-h-screen grid place-items-center text-text-muted text-sm">
        Cargando sesión…
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // Nota: la contraseña temporal (`mustChangePassword`) ya NO fuerza un redirect
  // a /configuracion — atrapaba al admin en esa página. El cambio de la clave por
  // defecto queda como recomendación (el backend sigue limpiando el flag cuando se
  // cambia). El acceso normal manda al destino solicitado (Dashboard).
  return <Outlet />;
}
